import { useQueryClient } from '@tanstack/react-query'
import getEnvironment from 'app/environment'
import { t } from 'app/i18l'
import { navigationRef } from 'app/linking'
import { useServer } from 'app/server/context'
import { useAuthentication } from 'authentication/context'
import { useChatConfig } from 'chat/config/queries/get'
import {
  removeConversationFromCache,
  updateConversationLastViewedTimestamp,
} from 'chat/conversations/queries/shared/utils'
import {
  addMentionDataToCache,
  addMessagesDataToCache,
  removeMessageDataFromCache,
  updateReactionData,
  updateReadByCount,
} from 'chat/messages/queries/shared/utils'
import { useRefetchMessagingContext } from 'chat/refetch_messaging_context'
import keys from 'chat/shared/queries/keys'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Platform } from 'react-native'
import { errorAlert } from 'shared/alerts'
import { MINS_TO_MS } from 'shared/constants'
import { useUser } from 'users/queries/detail'
import { WebSocketMessage } from './types'

export type ChatWebsocketContextValue = {
  sendWebsocketMessage: (wsMessage: WebSocketMessage) => void
}

export const ChatWebsocketContext = createContext(
  {} as ChatWebsocketContextValue
)

interface Props {
  children: React.ReactNode
}

interface WebsocketConnection {
  userId: string
  connectionId: string
}

type ConnectionStatus = 'OPENING' | 'OPEN' | 'CLOSED' | 'ERROR'

export function ChatWebsocketProvider(props: Props) {
  const websocket = useRef<WebSocket | null>(null)
  const [accessToken, setAccessToken] = useState<string | undefined>()
  const [savedWSConnection, setSavedWSConnection] = useState<
    WebsocketConnection | undefined
  >()
  const reconnectDelay = useRef<number>(1000) // 1 second
  const reconnectTimeoutId = useRef<NodeJS.Timeout | undefined>(undefined)
  const keepAliveIntervalId = useRef<NodeJS.Timeout | undefined>(undefined)
  const intentionallyClosed = useRef(false)
  const doConnectRef = useRef<() => void>(() => null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>()
  const { data: user } = useUser()
  const { setShouldRefetchConversations } = useRefetchMessagingContext()
  const { data: chatConfig } = useChatConfig()
  const { accessToken: userAccessToken } = useServer()
  const { status } = useAuthentication()
  const { localBaseURLInfo } = useServer()
  const client = useQueryClient()
  const { WEBSOCKETS_ENV } = getEnvironment()
  const wsUrl =
    Platform.OS === 'web'
      ? WEBSOCKETS_ENV === 'prod'
        ? 'wss://api.nextforcetechnology.com/websocket/chat'
        : 'wss://api-staging.nextforcetechnology.com/websocket/chat'
      : `wss://gdd2en0iq1.execute-api.us-west-2.amazonaws.com/${WEBSOCKETS_ENV}`

  const sendWebsocketMessage = useCallback(
    (wsMessage: WebSocketMessage) => {
      if (websocket.current && websocket.current.readyState === 1) {
        const tokenToSend =
          Platform.OS === 'web' ? undefined : userAccessToken ?? accessToken

        // Native requires token in payload for authentication; web uses HttpOnly cookie
        if (Platform.OS !== 'web' && !tokenToSend) {
          return
        }

        if (user?.id && localBaseURLInfo?.endpoint) {
          const payload: Record<string, unknown> = {
            ...wsMessage,
            userId: user?.id,
            baseURL: localBaseURLInfo?.endpoint,
          }

          if (Platform.OS !== 'web' && tokenToSend) {
            payload.token = tokenToSend
          }

          try {
            websocket.current.send(JSON.stringify(payload))
          } catch (error) {
            console.error('[ChatWebSocket] send error', error)
          }
        }
      }
    },
    [
      accessToken,
      localBaseURLInfo?.endpoint,
      user?.id,
      userAccessToken,
      websocket,
    ]
  )

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutId.current || status !== 'loggedIn') {
      return
    }

    const delay = reconnectDelay.current
    reconnectTimeoutId.current = setTimeout(() => {
      reconnectTimeoutId.current = undefined
      doConnectRef.current()
    }, delay)
    reconnectDelay.current = Math.min(delay * 2, 60 * 1000)
  }, [status])

  const doConnect = useCallback(
    (tokenOverride?: string) => {
      intentionallyClosed.current = false

      const tokenForConnection =
        Platform.OS === 'web'
          ? undefined
          : tokenOverride ?? userAccessToken ?? accessToken

      const socket = new WebSocket(wsUrl)

      socket.onopen = () => {
        if (websocket.current !== socket) {
          socket.close()
          return
        }

        if (socket.readyState === 1) {
          try {
            const sendAuthenticatedMessage = (wsMessage: WebSocketMessage) => {
              if (websocket.current !== socket) {
                return
              }

              if (websocket.current && websocket.current.readyState === 1) {
                if (Platform.OS !== 'web' && !tokenForConnection) {
                  return
                }

                if (user?.id && localBaseURLInfo?.endpoint) {
                  const payload: Record<string, unknown> = {
                    ...wsMessage,
                    userId: user?.id,
                    baseURL: localBaseURLInfo?.endpoint,
                  }

                  if (Platform.OS !== 'web' && tokenForConnection) {
                    payload.token = tokenForConnection
                  }

                  try {
                    websocket.current.send(JSON.stringify(payload))
                  } catch (error) {
                    console.error('[ChatWebSocket] send error', error)
                  }
                }
              }
            }

            sendAuthenticatedMessage({
              action: 'setUser',
            })
            setConnectionStatus('OPEN')
            // Reset the backoff now that we have a healthy connection, so a
            // future drop reconnects quickly instead of inheriting a long delay.
            reconnectDelay.current = 1000
            clearTimeout(reconnectTimeoutId.current)
            reconnectTimeoutId.current = undefined
            // Catch up on anything that was pushed while the socket was down:
            // the socket delivers live events only, so a reconnect must refetch
            // messages + conversations to backfill what was missed.
            client
              .invalidateQueries({ queryKey: keys.messages() })
              .catch(e => null)
            setShouldRefetchConversations(true)
            // Keep the connection warm: API Gateway drops idle websockets
            // (~10 min), so re-register periodically to reset the idle timer.
            clearInterval(keepAliveIntervalId.current)
            keepAliveIntervalId.current = setInterval(() => {
              sendAuthenticatedMessage({ action: 'setUser' })
            }, 4 * MINS_TO_MS)
          } catch (error) {
            console.error('Error saving connection ', error)
          }
        } else if (socket.readyState !== 1) {
          socket.close()
          return
        }
      }

      socket.onerror = err => {
        if (websocket.current !== socket) {
          return
        }

        setConnectionStatus('ERROR')
        console.error('[ChatWebSocket] socket error', err)
        socket.close()
      }

      socket.onclose = event => {
        const isCurrentSocket = websocket.current === socket

        if (isCurrentSocket) {
          setConnectionStatus('CLOSED')
          setSavedWSConnection(undefined)
          websocket.current = null
          clearInterval(keepAliveIntervalId.current) // stop pinging a dead socket
        }

        if (
          !isCurrentSocket ||
          intentionallyClosed.current ||
          status !== 'loggedIn'
        ) {
          return
        }

        scheduleReconnect()
      }

      socket.onmessage = event => {
        if (websocket.current !== socket) {
          return
        }

        if (event && event.data) {
          const eventData = JSON.parse(event.data)

          if (eventData?.type === 'add') {
            addMessagesDataToCache(client, [eventData], chatConfig).catch(
              e => null
            )
          } else if (eventData?.type === 'remove') {
            removeMessageDataFromCache(client, eventData).catch(e => null)
          } else if (eventData?.reactions) {
            updateReactionData(client, eventData, user).catch(e => null)
          } else if (eventData?.mentions) {
            addMentionDataToCache(client, eventData).catch(e => null)
          } else if (
            eventData?.type === 'custom' ||
            eventData?.type === 'dm' ||
            eventData?.type === 'adhoc'
          ) {
            setShouldRefetchConversations(true)
          } else if (eventData?.removedConversationId) {
            removeConversationFromCache(client, eventData.removedConversationId)
              .then(() => {
                const currentRoute = navigationRef?.current?.getCurrentRoute()
                const currentChatGroupId =
                  currentRoute?.name === 'messages' &&
                  ((currentRoute?.params as any).id as string)
                if (currentChatGroupId === eventData.removedConversationId) {
                  errorAlert(
                    {
                      name: 'CONVERSATION_DELETED',
                      message: t('conversationDeletedDescription'),
                    },
                    t('conversationDeleted')
                  )
                  navigationRef.navigate('conversations', {
                    screen:
                      user?.role === 'observer' || user?.role === 'admin'
                        ? 'groups'
                        : 'allConversations',
                  })
                }
              })
              .catch(e => null)
          } else if (
            eventData?.oldConversationId &&
            eventData?.updatedConversation
          ) {
            setShouldRefetchConversations(true)
          } else if (eventData?.messageIds) {
            updateReadByCount(client, eventData).catch(e => null)
          } else if (eventData?.lastViewedTimestamp) {
            updateConversationLastViewedTimestamp(
              client,
              eventData?.conversationId,
              eventData?.lastViewedTimestamp
            ).catch(e => null)
          }
          if (eventData.userId && eventData.connectionId) {
            setSavedWSConnection({
              userId: eventData.userId,
              connectionId: eventData.connectionId,
            })
          } else {
            setShouldRefetchConversations(true)
          }
        }
      }
      websocket.current = socket
    },
    [
      accessToken,
      chatConfig,
      client,
      localBaseURLInfo?.endpoint,
      scheduleReconnect,
      setShouldRefetchConversations,
      status,
      user,
      userAccessToken,
      wsUrl,
    ]
  )

  useEffect(() => {
    if (status === 'loggedIn') {
      if (userAccessToken && userAccessToken !== accessToken) {
        setAccessToken(userAccessToken)
      }
    } else if (status === 'loggedOut') {
      setAccessToken(undefined)
    }
  }, [accessToken, status, userAccessToken])

  doConnectRef.current = doConnect

  // For native: close and reconnect when the server token changes so the new socket
  // is created with the fresh auth token instead of the stale closure value.
  useEffect(() => {
    if (Platform.OS === 'web') {
      return
    }

    if (accessToken !== userAccessToken && websocket.current !== null) {
      const oldSocket = websocket.current
      websocket.current = null
      setSavedWSConnection(undefined)
      setConnectionStatus(undefined)
      clearInterval(keepAliveIntervalId.current)
      oldSocket.close(1000, 'token refresh')
    }
  }, [accessToken, userAccessToken])

  useEffect(() => {
    if (status === 'loggedIn') {
      const nativeTokenReady = Platform.OS === 'web' || !!userAccessToken

      if (
        !savedWSConnection &&
        connectionStatus === undefined &&
        !websocket.current &&
        user?.id &&
        localBaseURLInfo?.endpoint &&
        // On web: authenticate via HttpOnly cookie; on native: require bearer token
        nativeTokenReady
      ) {
        doConnect(userAccessToken)
      }
    } else if (status === 'loggedOut') {
      intentionallyClosed.current = true
      clearTimeout(reconnectTimeoutId.current)
      const currentSocket = websocket.current
      websocket.current = null
      setSavedWSConnection(undefined)
      if (currentSocket) {
        clearInterval(keepAliveIntervalId.current)
        currentSocket.close(1000, 'provider stopped')
      }
    }
  }, [
    connectionStatus,
    doConnect,
    localBaseURLInfo?.endpoint,
    savedWSConnection,
    status,
    user?.id,
    userAccessToken,
  ])

  // Stop the keepalive when the provider unmounts.
  useEffect(() => {
    return () => {
      intentionallyClosed.current = true
      clearTimeout(reconnectTimeoutId.current)
      const currentSocket = websocket.current
      websocket.current = null
      if (currentSocket) {
        clearInterval(keepAliveIntervalId.current)
        currentSocket.close(1000, 'provider stopped')
      }
    }
  }, [])

  const value: ChatWebsocketContextValue = {
    sendWebsocketMessage,
  }

  return (
    <ChatWebsocketContext.Provider value={value}>
      {props.children}
    </ChatWebsocketContext.Provider>
  )
}

export function useChatWebsocketContext() {
  return useContext<ChatWebsocketContextValue>(ChatWebsocketContext)
}
